import { css } from '@emotion/react';
import styled from '@emotion/styled';

import { colors } from '../../colors';
import type {
  StyledDropdownContainerProps,
  StyledDropdownIconProps,
  StyledDropdownMenuProps,
  StyledDropdownOptionProps,
  StyledDropdownTriggerProps,
} from './types';

export const DropdownContainer = styled.div<StyledDropdownContainerProps>`
  box-sizing: border-box;
  position: relative;
  display: flex;
  align-items: center;
  background: ${({ $styleProps }) => $styleProps?.unitBackgroundColor ?? colors.gray[100]};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  user-select: none;
  transition: all 0.2s ease-in-out;

  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }

  ${({ $size }) => {
    switch ($size) {
      case 'small':
        return css`
          width: 50px;
          height: 32px;
          padding: 0 8px;
          font-size: 12px;
        `;
      case 'large':
        return css`
          width: 70px;
          height: 48px;
          padding: 0 12px;
          font-size: 16px;
        `;
      case 'medium':
      default:
        return css`
          width: 60px;
          height: 40px;
          padding: 0 10px;
          font-size: 14px;
        `;
    }
  }}

  ${({ $position }) =>
    $position === 'left'
      ? css`
          border-top-left-radius: 4px;
          border-bottom-left-radius: 4px;
          border-right: none;
        `
      : css`
          border-top-right-radius: 4px;
          border-bottom-right-radius: 4px;
          border-left: none;
        `}

  &:hover:not([disabled]) {
    background: ${({ $styleProps }) => $styleProps?.unitHoverBackgroundColor ?? colors.gray[100]};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const DropdownTrigger = styled.div<StyledDropdownTriggerProps>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  color: ${({ $styleProps }) => $styleProps?.unitTextColor ?? colors.gray[700]};
`;

export const DropdownIcon = styled.div<StyledDropdownIconProps>`
  margin-left: 6px;
  display: flex;
  align-items: center;
  color: ${({ $disabled }) => ($disabled ? colors.gray[400] : colors.gray[500])};
  transform: ${({ $open }) => ($open ? 'rotate(180deg)' : 'rotate(0deg)')};
  transition: transform 0.2s ease-in-out;
`;

export const DropdownMenu = styled.div<StyledDropdownMenuProps>`
  box-sizing: border-box;
  position: absolute;
  top: 100%;
  ${({ $position }) => ($position === 'left' ? 'left: 0;' : 'right: 0;')}
  min-width: 160px;
  background: ${colors.gray[100]};
  border: 1px solid ${colors.gray[200]};
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  visibility: ${({ $open }) => ($open ? 'visible' : 'hidden')};
  transform: ${({ $open }) => ($open ? 'translateY(4px)' : 'translateY(-4px)')};
  transition: all 0.2s ease-in-out;
  max-height: 200px;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: ${colors.gray[100]};
  }

  &::-webkit-scrollbar-thumb {
    background: ${colors.gray[300]};
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${colors.gray[400]};
  }
`;

export const DropdownOption = styled.div<StyledDropdownOptionProps>`
  box-sizing: border-box;
  padding: ${({ $size }) => {
    switch ($size) {
      case 'small':
        return '6px 12px';
      case 'large':
        return '10px 16px';
      default:
        return '8px 14px';
    }
  }};
  cursor: pointer;
  color: ${({ $selected }) => ($selected ? colors.gray[100] : colors.gray[700])};
  background: ${({ $selected }) => ($selected ? colors.primary[300] : 'transparent')};
  font-size: ${({ $size }) => {
    switch ($size) {
      case 'small':
        return '12px';
      case 'large':
        return '16px';
      default:
        return '14px';
    }
  }};
  font-weight: ${({ $selected }) => ($selected ? '600' : '400')};
  transition: all 0.15s ease-in-out;

  &:hover {
    background: ${({ $selected }) => ($selected ? colors.primary[300] : colors.primary[50])};
    color: ${({ $selected }) => ($selected ? colors.gray[100] : colors.primary[300])};
  }

  &:first-of-type {
    border-top-left-radius: 7px;
    border-top-right-radius: 7px;
  }

  &:last-of-type {
    border-bottom-left-radius: 7px;
    border-bottom-right-radius: 7px;
  }
`;